import type { Security } from '@ovComponents/3-pattern/securitiesSelectionTable/types';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

type InitializeLiveSecuritiesContextValue = {
  /** Rows as the table holds them now, including edits not yet debounced to context. */
  liveSecurities: Security[] | undefined;
  setLiveSecurities: (rows: Security[]) => void;
  registerFlushPendingSecurities: (flush: (() => void) | null) => void;
  /** Lands any debounced row write before Initialize is handled. */
  flushPendingSecurities: () => void;
};

const InitializeLiveSecuritiesContext =
  createContext<InitializeLiveSecuritiesContextValue | null>(null);

export const InitializeLiveSecuritiesProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [liveSecurities, setLiveSecuritiesState] = useState<
    Security[] | undefined
  >();
  const flushRef = useRef<(() => void) | null>(null);

  const setLiveSecurities = useCallback((rows: Security[]) => {
    setLiveSecuritiesState(rows);
  }, []);

  const registerFlushPendingSecurities = useCallback(
    (flush: (() => void) | null) => {
      flushRef.current = flush;
    },
    [],
  );

  const flushPendingSecurities = useCallback(() => {
    flushRef.current?.();
  }, []);

  const value = useMemo(
    () => ({
      liveSecurities,
      setLiveSecurities,
      registerFlushPendingSecurities,
      flushPendingSecurities,
    }),
    [
      liveSecurities,
      setLiveSecurities,
      registerFlushPendingSecurities,
      flushPendingSecurities,
    ],
  );

  return (
    <InitializeLiveSecuritiesContext.Provider value={value}>
      {children}
    </InitializeLiveSecuritiesContext.Provider>
  );
};

/** @internal exported for tests */
export const useInitializeLiveSecurities = () =>
  useContext(InitializeLiveSecuritiesContext);
